import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 提示消息组件
 * 提供统一的消息提示UI，支持多种类型和自动关闭
 *
 * @example
 * ```tsx
 * // 基本用法
 * <Toast>操作成功</Toast>
 *
 * // 使用不同类型
 * <Toast type="success">保存成功</Toast>
 * <Toast type="error">操作失败</Toast>
 * <Toast type="warning">请注意</Toast>
 * <Toast type="info">提示信息</Toast>
 *
 * // 自动关闭
 * <Toast
 *   type="success"
 *   autoClose={true}
 *   duration={3000}
 *   onClose={() => console.log('关闭')}
 * >
 *   3秒后自动关闭
 * </Toast>
 *
 * // 带图标和操作
 * <Toast
 *   type="info"
 *   icon="ℹ️"
 *   action={<button>撤销</button>}
 *   closable={true}
 * >
 *   操作已完成
 * </Toast>
 * ```
 */
import { forwardRef, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../utils/cn.js';
/**
 * 获取消息类型样式
 */
const getTypeStyles = (type) => {
    const styles = {
        default: {
            bg: 'bg-static-eggshell',
            border: 'border-static-cream-yellow',
            icon: 'text-gray-700',
        },
        success: {
            bg: 'bg-static-eggshell',
            border: 'border-interactive-emerald-flash',
            icon: 'text-interactive-emerald-flash',
        },
        error: {
            bg: 'bg-static-eggshell',
            border: 'border-interactive-coral-red',
            icon: 'text-interactive-coral-red',
        },
        warning: {
            bg: 'bg-static-eggshell',
            border: 'border-static-beach-orange',
            icon: 'text-static-beach-orange',
        },
        info: {
            bg: 'bg-static-eggshell',
            border: 'border-static-sea-blue',
            icon: 'text-static-sea-blue',
        },
    };
    return styles[type];
};
/**
 * 获取默认图标
 */
const getDefaultIcon = (type) => {
    const icons = {
        default: null,
        success: (_jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" }) })),
        error: (_jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) })),
        warning: (_jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z", clipRule: "evenodd" }) })),
        info: (_jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z", clipRule: "evenodd" }) })),
    };
    return icons[type];
};
/**
 * 提示消息组件
 *
 * 用于显示操作反馈、通知和临时消息的组件
 */
export const Toast = forwardRef(({ type = 'default', closable = true, autoClose = false, duration = 3000, onClose, icon, action, title, visible = true, className, children, ...props }, ref) => {
    const typeStyles = getTypeStyles(type);
    const defaultIcon = icon !== undefined ? icon : getDefaultIcon(type);
    const toastRef = useRef(null);
    // 自动关闭逻辑
    useEffect(() => {
        if (autoClose && visible && duration > 0) {
            const timer = setTimeout(() => {
                onClose?.();
            }, duration);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [autoClose, visible, duration, onClose]);
    // 点击外部关闭逻辑
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (toastRef.current && !toastRef.current.contains(event.target)) {
                onClose?.();
            }
        };
        if (visible) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [visible, onClose]);
    if (!visible) {
        return null;
    }
    const toastClasses = cn('flex items-start p-4 rounded-lg border-2 shadow-lg transition-all duration-300', 'max-w-md w-full', typeStyles.bg, typeStyles.border, className);
    const handleClose = () => {
        onClose?.();
    };
    const toastContent = (_jsxs("div", { ref: toastRef, className: toastClasses, role: "alert", "aria-live": "polite", onClick: e => e.stopPropagation(), ...props, children: [defaultIcon && (_jsx("div", { className: cn('flex-shrink-0 mr-3', typeStyles.icon), children: defaultIcon })), _jsxs("div", { className: "flex-1 min-w-0", children: [title && _jsx("h4", { className: cn('text-sm font-medium mb-1', typeStyles.icon), children: title }), children && _jsx("div", { className: cn('text-sm', typeStyles.icon), children: children }), action && _jsx("div", { className: "mt-3", children: action })] }), closable && (_jsxs("button", { type: "button", className: "flex-shrink-0 ml-3 rounded-md p-1 hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current", onClick: handleClose, "aria-label": "Close", children: [_jsx("span", { className: "sr-only", children: "Close" }), _jsx("svg", { className: "w-4 h-4 text-gray-400", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) })] }))] }));
    return ReactDOM.createPortal(_jsx("div", { ref: ref, className: "fixed inset-0 z-50 flex items-center justify-center", onClick: handleClose, children: toastContent }), document.body);
});
Toast.displayName = 'Toast';
export default Toast;
//# sourceMappingURL=Toast.js.map